import {
  Button,
  Checkbox,
  Divider,
  Group,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { client } from "@/api-client";
import type { NotificationSettingsInput } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import { UniversalRoutes, useNavigator } from "@/routers/routes";
import { useUser } from "@/UserContext";
import { getPushSettings, setPushSettings } from "@/utils/pushSettings";

type NotificationFormData = {
  email: NotificationSettingsInput;
  push: NotificationSettingsInput;
};

const NotificationSettingsWhenLoaded = ({
  data,
}: {
  data: NotificationSettingsInput;
}) => {
  const { navigate } = useNavigator();
  const { role } = useUser();

  // Form state with separate email and push objects
  const form = useForm<NotificationFormData>({
    mode: "uncontrolled",
    initialValues: {
      email: data,
      push: getPushSettings(),
    },
  });

  const trainerMutation = client.useMutation(
    "post",
    "/trainer/profile/notifications",
  );
  const ownerMutation = client.useMutation(
    "post",
    "/owner/profile/notifications",
  );

  const mutation = role === "trainer" ? trainerMutation : ownerMutation;

  const handleSubmit = async (values: NotificationFormData) => {
    // Save email settings to server
    const emailSettings = values.email;

    // Save push settings to localStorage
    setPushSettings(values.push);

    await mutation.mutateAsync({ body: emailSettings });
    navigate({
      path: UniversalRoutes.PROFILE.HOME,
    });
  };

  const NotificationTypeSection = ({
    type,
    label,
    description,
    disabled = false,
  }: {
    type: keyof NotificationSettingsInput;
    label: string;
    description: string;
    disabled?: boolean;
  }) => (
    <Stack gap="xs">
      <Text fw={500}>{label}</Text>
      <Text size="sm" c="dimmed">
        {description}
      </Text>
      <Group gap="xl">
        <Checkbox
          label="Email"
          key={form.key(`email.${type}`)}
          {...form.getInputProps(`email.${type}`, { type: "checkbox" })}
          disabled={disabled}
        />
        <Checkbox
          label="Push notification"
          key={form.key(`push.${type}`)}
          {...form.getInputProps(`push.${type}`, { type: "checkbox" })}
        />
      </Group>
    </Stack>
  );

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="lg">
        <Title order={2}>Notification Settings</Title>
        <Text size="sm" c="dimmed">
          Choose how you'd like to receive each type of notification
        </Text>

        <NotificationTypeSection
          type="updates"
          label="System updates"
          description="Important updates about sessions and bookings"
          disabled
        />

        <Divider />

        <NotificationTypeSection
          type="messages"
          label="Messages"
          description="Notifications for new messages and chat activity"
        />

        <Divider />

        <NotificationTypeSection
          type="news"
          label="News and offers"
          description="News and updates about Woofapy"
        />

        <Button type="submit" loading={mutation.isPending} mt="md">
          Save Settings
        </Button>
      </Stack>
    </form>
  );
};

export const NotificationSettings = () => {
  const { role } = useUser();

  const queryPath =
    role === "trainer"
      ? "/trainer/profile/notifications"
      : "/owner/profile/notifications";
  const query = client.useQuery("get", queryPath);

  if (!query.data) {
    return <FancyLoader />;
  }

  return (
    <Stack>
      <Header title="Notification Settings" />
      <NotificationSettingsWhenLoaded data={query.data} />
    </Stack>
  );
};
